import React, { createContext, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import SiteSetupStorage from 'src/js/modules/site-setup/helpers/site-setup-storage';
import getCookie from 'src/js/utils/get-cookie';
import useCategoryLoadingBySlug from 'src/js/modules/site-setup/hooks/use-category-loading-by-slug';
import {
  WS_BUSINESS_CATEGORY_COOKIE,
  WS_BUSINESS_DESCRIPTION_COOKIE,
} from 'src/js/constants/common';

const SiteSetupContext = createContext({});

const SiteSetupContextProvider = ({ children }) => {
  const [setupInfo, setSetupInfo] = useState(() => {
    const storageData = SiteSetupStorage.getData();
    const initialData = { ...storageData };

    if (!('businessCategorySlug' in storageData)) {
      const businessCategorySlug = getCookie(WS_BUSINESS_CATEGORY_COOKIE);
      if (businessCategorySlug) {
        initialData.businessCategorySlug = decodeURIComponent(businessCategorySlug);
      }
    }

    if (!('businessDescription' in storageData)) {
      const businessDescription = getCookie(WS_BUSINESS_DESCRIPTION_COOKIE);
      if (businessDescription) {
        initialData.businessDescription = decodeURIComponent(businessDescription);
      }
    }

    return initialData;
  });

  useEffect(() => {
    if (Object.keys(setupInfo).length > 0) {
      SiteSetupStorage.setData(setupInfo);
    }
  }, [setupInfo]);

  const updateData = useCallback((data) => {
    setSetupInfo((info) => ({
      ...info,
      ...data,
    }));
  }, []);

  const setData = useCallback((data) => {
    setSetupInfo(data);
  }, []);

  const categorySlug = !setupInfo.businessCategory ? setupInfo.businessCategorySlug : null;

  const onCategoryLoad = useCallback(
    (data) => {
      if (data) {
        updateData({
          businessCategory: data.title,
          archetype: data.archetypes[0],
          templates: data.templates || [],
        });
      } else {
        updateData({
          businessCategorySlug: null,
        });
      }
    },
    [updateData]
  );

  const isCategoryLoaded = useCategoryLoadingBySlug(categorySlug, onCategoryLoad);

  const getData = (key) => {
    if (!key) {
      return setupInfo;
    }

    return setupInfo[key];
  };

  const getArchetype = () => getData('archetype');

  const api = {
    getArchetype,
    getData,
    isCategoryLoaded,
    setData,
    updateData,
  };

  return <SiteSetupContext.Provider value={api}>{children}</SiteSetupContext.Provider>;
};

SiteSetupContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { SiteSetupContext, SiteSetupContextProvider };
